// src/features/chat/components/SwipeableMessage.jsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Reply } from 'lucide-react';
import styles from './SwipeableMessage.module.css';

const SWIPE_THRESHOLD = 60;
const RESISTANCE = 0.5;
const MAX_SWIPE = 100;

const SwipeableMessage = ({
  children,
  onReply,
  disabled = false,
  variant = 'received',
}) => {
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const hasTriggeredRef = useRef(false);

  const [translateX, setTranslateX] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const triggerHaptic = useCallback(() => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }, []);

  const handleTouchStart = useCallback(
    (event) => {
      if (disabled) return;
      const touch = event.touches[0];
      startXRef.current = touch.clientX;
      currentXRef.current = touch.clientX;
      isDraggingRef.current = true;
      hasTriggeredRef.current = false;
      setIsActive(true);
    },
    [disabled]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (!isDraggingRef.current || disabled) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - startXRef.current;
      currentXRef.current = touch.clientX;

      if (deltaX <= 0) {
        setTranslateX(0);
        return;
      }

      let adjustedDelta = deltaX;
      if (deltaX > SWIPE_THRESHOLD) {
        adjustedDelta = SWIPE_THRESHOLD + (deltaX - SWIPE_THRESHOLD) * RESISTANCE;
        if (!hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          triggerHaptic();
        }
      }

      adjustedDelta = Math.min(adjustedDelta, MAX_SWIPE);
      setTranslateX(adjustedDelta);
    },
    [disabled, triggerHaptic]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDraggingRef.current || disabled) return;
    isDraggingRef.current = false;
    setIsActive(false);

    const deltaX = currentXRef.current - startXRef.current;
    if (deltaX >= SWIPE_THRESHOLD && onReply) {
      triggerHaptic();
      onReply();
    }

    setTranslateX(0);
  }, [disabled, onReply, triggerHaptic]);

  useEffect(() => {
    return () => {
      isDraggingRef.current = false;
    };
  }, []);

  const indicatorOpacity = Math.min(translateX / SWIPE_THRESHOLD, 1);
  const indicatorScale = 0.5 + indicatorOpacity * 0.5;
  const isTriggered = translateX >= SWIPE_THRESHOLD;

  return (
    <div
      ref={containerRef}
      className={styles.swipeableContainer}
      data-variant={variant}
      data-active={isActive}
    >
      <div
        className={styles.swipeIndicator}
        style={{
          opacity: indicatorOpacity,
          transform: `translateY(-50%) scale(${indicatorScale})`,
        }}
        data-triggered={isTriggered}
      >
        <Reply size={20} />
      </div>

      <div
        className={styles.swipeableContent}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isActive
            ? 'none'
            : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableMessage;
